import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import TrustBar from "@/pages/home/components/TrustBar";
import ServicesOverview from "@/pages/home/components/ServicesOverview";
import ProcessSection from "@/pages/home/components/ProcessSection";
import Testimonials from "@/pages/home/components/Testimonials";
import FinalCTA from "@/pages/home/components/FinalCTA";
import MidPageCTA from "@/pages/home/components/MidPageCTA";
import { Link } from "react-router-dom";
import { useState } from "react";

const NY_CITIES = [
  "Long Island (Nassau & Suffolk)",
  "Queens",
  "The Bronx",
  "Manhattan",
  "Staten Island",
  "Westchester County",
  "Rockland County",
  "Brooklyn",
  "Garden City",
  "Great Neck",
  "Hempstead",
  "Hicksville",
  "Levittown",
  "Mineola",
  "Plainview",
  "Syosset",
  "Flushing",
  "Jamaica",
  "New Rochelle",
  "Yonkers",
];

const NY_QA = [
  {
    q: "Who provides pre-purchase used car inspections in New York?",
    a: "Lemon Protector provides professional pre-purchase used car inspections throughout New York — including Long Island, Queens, the Bronx, Manhattan, Westchester County, and beyond. We travel on-site to the vehicle's location, perform a complete 450-point inspection, and give you a direct call from your actual inspector. Starting at $139.",
  },
  {
    q: "What inspection services are available in New York?",
    a: "All of Lemon Protector's core services are available throughout New York: 450-point pre-purchase inspection, detailed written report with photos, free title and vehicle history report, book value appraisal, and personal phone consultation with the inspector who evaluated the car.",
  },
  {
    q: "Do you serve out-of-state buyers purchasing in New York?",
    a: "Yes. Lemon Protector welcomes out-of-state buyers purchasing vehicles in New York. We coordinate directly with the seller on your behalf, inspect the car wherever it's located, and provide a complete report so you can make a confident purchase decision from anywhere in the country.",
  },
];

const NY_FAQ = [
  { q: "Who offers pre-purchase used car inspections in New York?", a: "Lemon Protector offers professional 450-point pre-purchase used car inspections throughout New York — Long Island, Queens, the Bronx, Westchester, and more. We travel to the vehicle's location. Starting at $139 with direct inspector consultation after every job." },
  { q: "How much does a used car inspection cost in New York?", a: "Lemon Protector's pre-purchase car inspections in New York start at $139. That includes the full 450-point inspection, written report with photos, free title search, vehicle history report, and book value appraisal — no hidden fees." },
  { q: "How long does a pre-purchase inspection take in New York?", a: "A typical on-site 450-point inspection in New York takes one to two hours. After the inspection, your inspector calls you directly to walk through every finding. The detailed written report is emailed the same day." },
];

export default function LocationNewYork() {
  const [activeQA, setActiveQA] = useState<number | null>(0);
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);

  return (
    <div className="bg-brand-black min-h-screen font-sans">
      <Navbar />
      <main>
        {/* 1. Hero */}
        <section className="relative w-full min-h-[680px] flex items-center overflow-hidden pt-20">
          <div className="absolute inset-0 w-full h-full">
            <img
              src="https://public.readdy.ai/ai/img_res/ce7cb3e059f22c05db462f416a569103.jpg"
              alt="Pre-purchase used car inspections in New York — Lemon Protector"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-black/95 via-brand-black/78 to-brand-black/40" />
            <div className="absolute inset-0 bg-gradient-to-b from-brand-black/40 via-transparent to-brand-black/82" />
          </div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-20">
            <div className="max-w-2xl">
              <p className="font-sans text-xs tracking-widest uppercase text-brand-gold mb-5 flex items-center gap-3">
                <span className="w-8 h-px bg-brand-gold" />
                Serving New York
              </p>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-brand-white leading-tight mb-5">
                Pre-Purchase Car Inspections<br />
                <span className="text-brand-gold italic">in New York</span>
              </h1>
              <p className="font-sans text-base md:text-lg text-brand-gray-light leading-relaxed max-w-xl mb-6">
                Professional 450-point on-site inspections across Long Island, Queens, the Bronx, Westchester, and all of New York. We travel to the vehicle — starting at $139.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-gold text-brand-black font-sans font-bold text-sm tracking-wide rounded-full hover:bg-brand-gold-light transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-search-line" />
                  Book a New York Inspection
                </Link>
                <a
                  href="tel:5169833800"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-brand-gold/50 text-brand-gold font-sans font-medium text-sm rounded-full hover:bg-brand-gold/10 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line" />
                  (516) 983-3800
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* 2. Trust Bar */}
        <TrustBar />

        {/* 3. Services Overview */}
        <ServicesOverview />

        {/* 4. AI Answer Block */}
        <section className="bg-brand-panel py-24 lg:py-32">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="flex flex-col lg:flex-row gap-16">
              <div className="lg:w-1/3">
                <p className="font-sans text-xs tracking-widest uppercase text-brand-gold mb-4 flex items-center gap-3">
                  <span className="w-8 h-px bg-brand-gold" />
                  New York Coverage
                </p>
                <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-brand-white leading-tight mb-5">
                  Inspections Across All of New York
                </h2>
                <p className="font-sans text-sm text-brand-gray leading-relaxed">
                  Buying a used car anywhere in New York? We come to the vehicle — no logistics, no hassle, total convenience.
                </p>
                <div className="mt-10 font-serif text-[110px] leading-none text-brand-gold/8 select-none">&ldquo;</div>
              </div>
              <div className="lg:w-2/3 flex flex-col gap-4">
                {NY_QA.map((item, i) => (
                  <div
                    key={i}
                    className={`rounded-2xl border transition-all duration-300 overflow-hidden ${activeQA === i ? "border-brand-gold/40 bg-brand-charcoal" : "border-brand-gold/10 bg-brand-charcoal/50 hover:border-brand-gold/25"}`}
                  >
                    <button className="w-full text-left px-6 py-5 flex items-start justify-between gap-4 cursor-pointer" onClick={() => setActiveQA(activeQA === i ? null : i)}>
                      <h3 className="font-serif text-base text-brand-white font-medium leading-snug">{item.q}</h3>
                      <div className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                        <i className={`text-brand-gold text-lg ${activeQA === i ? "ri-subtract-line" : "ri-add-line"}`} />
                      </div>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${activeQA === i ? "max-h-64 pb-6" : "max-h-0"}`}>
                      <p className="px-6 font-sans text-sm text-brand-gray-light leading-relaxed">{item.a}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 5. Process */}
        <ProcessSection />

        {/* 6. Why Choose Us — NY Relevance */}
        <section className="bg-brand-charcoal py-24 lg:py-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex flex-col lg:flex-row gap-12 items-start">
              <div className="lg:w-1/2">
                <p className="font-sans text-xs tracking-widest uppercase text-brand-gold mb-4 flex items-center gap-3">
                  <span className="w-8 h-px bg-brand-gold" />
                  Why New York Buyers Choose Us
                </p>
                <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-brand-white leading-tight mb-5">
                  The New York Used Car Market Demands an Independent Eye
                </h2>
                <p className="font-sans text-base text-brand-gray leading-relaxed mb-5">
                  New York is one of the most competitive used car markets in the country. Prices move fast and sellers present vehicles at their absolute best. Lemon Protector gives New York buyers the independent, unbiased evaluation they need to make smart decisions and avoid costly mistakes.
                </p>
                <p className="font-sans text-sm text-brand-gray leading-relaxed mb-8">
                  With over 25 years of experience across Long Island, Queens, the Bronx, Manhattan, Westchester, and the outer boroughs, we know the local market — and the problems that hide within it.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-7 py-4 bg-brand-gold text-brand-black font-sans font-bold text-sm tracking-wide rounded-full hover:bg-brand-gold-light transition-all whitespace-nowrap cursor-pointer"
                  >
                    Schedule a New York Inspection
                    <i className="ri-arrow-right-line" />
                  </Link>
                  <a
                    href="tel:5169833800"
                    className="inline-flex items-center gap-2 px-7 py-4 border border-brand-gold/30 text-brand-gold font-sans font-medium text-sm rounded-full hover:bg-brand-gold/8 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line" />
                    Call (516) 983-3800
                  </a>
                </div>
              </div>

              <div className="lg:w-1/2 bg-brand-dark border border-brand-gold/15 rounded-2xl p-8">
                <h3 className="font-serif text-xl font-semibold text-brand-white mb-6 flex items-center gap-3">
                  <i className="ri-map-pin-2-fill text-brand-gold" />
                  New York Locations We Serve
                </h3>
                <div className="grid grid-cols-2 gap-1.5">
                  {NY_CITIES.map((city) => (
                    <div key={city} className="flex items-center gap-2 font-sans text-sm text-brand-gray-light py-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-gold/60 shrink-0" />
                      {city}
                    </div>
                  ))}
                </div>
                <p className="mt-5 font-sans text-xs text-brand-gray">
                  Don&apos;t see your city?{" "}
                  <a href="tel:5169833800" className="text-brand-gold font-semibold hover:text-brand-gold-light transition-colors cursor-pointer">
                    Call us
                  </a>{" "}
                  — we very likely cover it.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 7. Service Areas - map */}
        <section className="bg-brand-dark py-24 lg:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-12">
              <p className="font-sans text-xs tracking-widest uppercase text-brand-gold mb-4 flex items-center justify-center gap-3">
                <span className="w-8 h-px bg-brand-gold" />
                Full Coverage Map
                <span className="w-8 h-px bg-brand-gold" />
              </p>
              <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-brand-white leading-tight mb-3">
                We Service All of New York — Plus NJ &amp; CT
              </h2>
              <p className="font-sans text-base text-brand-gray max-w-xl mx-auto">
                Our tri-state mobile inspection service covers the entire metro region. Out-of-state buyers purchasing in NY are also welcome.
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 rounded-2xl overflow-hidden border border-brand-gold/15 h-72 lg:h-96">
                <iframe
                  title="Lemon Protector New York Service Area Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d386000!2d-73.94996!3d40.6501!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY!5e0!3m2!1sen!2sus!4v1710000000000"
                  className="w-full h-full border-0 grayscale"
                  loading="lazy"
                  allowFullScreen
                />
              </div>
              <div className="flex flex-col gap-4">
                {[
                  { label: "New Jersey", desc: "Bergen, Essex, Hudson, Union, Passaic, Middlesex, Morris, Monmouth Counties", icon: "ri-map-pin-2-fill" },
                  { label: "Connecticut", desc: "Fairfield County, Greenwich, Stamford, Darien, Norwalk, New Haven County, Westport, Bridgeport", icon: "ri-map-pin-2-fill" },
                ].map((area) => (
                  <div key={area.label} className="bg-brand-charcoal border border-brand-gold/10 rounded-2xl p-6 hover:border-brand-gold/30 transition-colors flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-7 h-7 flex items-center justify-center">
                        <i className={`${area.icon} text-brand-gold text-lg`} />
                      </div>
                      <h3 className="font-serif text-lg font-semibold text-brand-white">{area.label}</h3>
                    </div>
                    <p className="font-sans text-sm text-brand-gray leading-relaxed">{area.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Mid CTA */}
        <MidPageCTA />

        {/* 8. Testimonials */}
        <Testimonials />

        {/* 9. FAQ — NY specific */}
        <section className="bg-brand-panel py-24 lg:py-32">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <div className="mb-14">
              <p className="font-sans text-xs tracking-widest uppercase text-brand-gold mb-4 flex items-center gap-3">
                <span className="w-8 h-px bg-brand-gold" />
                FAQ — New York
              </p>
              <h2 className="font-serif text-4xl font-semibold text-brand-white leading-tight">
                New York Car Inspection Questions
              </h2>
            </div>
            <div className="flex flex-col">
              {NY_FAQ.map((item, i) => (
                <div key={i} className={`border-b ${i === 0 ? "border-t border-brand-gold/15" : "border-brand-gold/15"}`}>
                  <button
                    className="w-full text-left py-6 flex items-start justify-between gap-6 cursor-pointer group"
                    onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                  >
                    <h4 className={`font-serif text-lg leading-snug transition-colors ${openFAQ === i ? "text-brand-gold" : "text-brand-white group-hover:text-brand-gold"}`}>
                      {item.q}
                    </h4>
                    <div className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                      <i className={`text-xl text-brand-gold ${openFAQ === i ? "ri-subtract-line" : "ri-add-line"}`} />
                    </div>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openFAQ === i ? "max-h-52 pb-6" : "max-h-0"}`}>
                    <p className="font-sans text-sm text-brand-gray-light leading-relaxed">{item.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 10. Final CTA */}
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
